import { useMutation } from 'react-query';
import usePlanStore, { CountryFormState } from 'store/planStore';
import { Countries } from 'shared/enums';
import moment from 'moment/moment';
import { savePlan } from '../../../../../../api/services/mediaPlanning/savePlan';
import { useError } from '../../../common/Errors/ErrorContext/ErrorContext';

function isDdxNullsSentinel(ddx: any) {
  return (
    ddx != null && ddx.type == null && ddx.value == null && ddx.totalDDX == null
  );
}

const useSavePlan = () => {
  const { generalInfoState, selectedCountryISOCode, setAlreadySavedPlanId } =
    usePlanStore();

  const { showError } = useError();

  const convertDatesToISO = (
    form: Partial<CountryFormState>
  ): Partial<CountryFormState> => {
    const convert = (date?: string | null) =>
      date ? moment(date, 'DD-MM-YYYY').format('YYYY-MM-DD') : date;

    return {
      ...form,
      startDate: convert(form.startDate) || '',
      availableTouchpoints: form.availableTouchpoints,
      offline: {
        ...form.offline,
        tv: form.offline?.tv
          ? {
              ...form.offline?.tv,
              ...(form.offline.tv.startDate && {
                startDate: moment(
                  form.offline.tv.startDate,
                  'DD-MM-YYYY'
                ).format('YYYY-MM-DD'),
              }),
              ...(form.offline.tv.endDate && {
                endDate: moment(form.offline.tv.endDate, 'DD-MM-YYYY').format(
                  'YYYY-MM-DD'
                ),
              }),
            }
          : undefined,
      },
      youTubeConfig: form.youTubeConfig && {
        ...form.youTubeConfig,
        ...(process.env.REACT_APP_API_VERSION !== 'v3' && {
          skipShare: form.youTubeConfig?.skipValue,
          unskipShare: form.youTubeConfig?.unSkipValue,
        }),
      },
      metaConfig: form.metaConfig
        ? {
            ...form.metaConfig,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            startDate: convert(form.metaConfig.startDate),
          }
        : undefined,
    };
  };

  const buildCountrySetupWithISO = (
    stored: Record<string, Partial<CountryFormState>>
  ): Record<string, Partial<CountryFormState>> =>
    Object.fromEntries(
      Object.entries(stored).map(([countryISO, form]) => {
        const converted = convertDatesToISO(form);

        const { ddx, ...rest } = converted as any;

        const base: Partial<CountryFormState> = {
          ...rest,
          calculatedBudget: (form as any).calculatedBudget ?? [],
        };

        if (isDdxNullsSentinel(ddx)) {
          return [countryISO, base];
        }

        const ddxToSend = ddx ?? { type: 'omitted' };

        return [countryISO, { ...base, ddx: ddxToSend }];
      })
    );

  const {
    mutate: saveMediaPlan,
    mutateAsync: asyncSaveMediaPlan,
    data: response,
    isLoading,
    isError,
  } = useMutation(
    async () => {
      const freshStoredCountryForms =
        usePlanStore.getState().storedCountryForms;

      const countrySetupWithISO = buildCountrySetupWithISO(
        freshStoredCountryForms as any
      );

      return savePlan({
        body: {
          campaignName: generalInfoState.campaignName,
          campaignCode: generalInfoState.campaignCode,
          division: generalInfoState.division,
          brand: generalInfoState.brand,
          category: generalInfoState.category,
          countrySetup: countrySetupWithISO,
        },
        countryISO: selectedCountryISOCode as Countries,
      });
    },
    {
      onError: (error: any) => {
        const errorMessage =
          error?.response?.data?.message || error.message || 'Unknown error';
        showError(errorMessage);
      },
      onSuccess: (res) => {
        setAlreadySavedPlanId(res.data.id);
      },
    }
  );

  return {
    saveMediaPlan,
    asyncSaveMediaPlan,
    data: response?.data,
    isLoading,
    isError,
  };
};

export default useSavePlan;
